import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { requestPasswordReset } from "../api/auth";
import { AuthShell } from "../components/AuthShell";
import { Button, Field } from "../components/Page";

/**
 * Request a password reset link.
 *
 * The API answers `{ sent: true }` whether or not the account exists, so this
 * page must never imply the address was recognised — it confirms the request
 * and stops there. Distinguishing the two cases here would undo the API's
 * deliberate anti-enumeration behaviour.
 */
export default function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(identifier.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the link. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Reset your password"
      lede="We will email you a link to choose a new one."
      footer={
        <Link to="/login" className="font-medium text-primary underline">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <p className="text-sm leading-relaxed text-ink-muted">
          If an account matches that phone number or email, a reset link is on its way. It is valid
          for a short time, so use it soon.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            id="identifier"
            name="identifier"
            label="Phone or email"
            autoComplete="username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />

          {error ? (
            <p role="alert" className="rounded-r border-l-[3px] border-danger bg-danger-wash px-3.5 py-2.5 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}